import { useCallback, useRef, useState } from 'react';

export function useGridOrdenacao({ configuracao }) {
  const [listaColunaOrdem, setListaColunaOrdem] = useState([]);
  const [tableKey, setTableKey] = useState(Date.now());
  const ignorarPrimeiroHandleChangeRef = useRef(false);

  const normalizarListaColunaOrdem = useCallback(lista => {
    const itens = Array.isArray(lista)
      ? lista.filter(i => i?.ColunaOrdem && i?.TipoOrdem)
      : [];

    // Dedup por ColunaOrdem (ultimo vence) mantendo ordem relativa do ultimo.
    const map = new Map();
    for (const item of itens) {
      const key = String(item.ColunaOrdem).trim();
      if (map.has(key)) map.delete(key);
      map.set(key, { ...item, ColunaOrdem: key });
    }

    const deduped = Array.from(map.values());

    // Se vier com Ordem, respeita para ordenar; caso contrario, mantem a ordem do map.
    const temOrdem = deduped.some(i => Number.isFinite(i?.Ordem));
    const ordenado = temOrdem
      ? deduped
          .slice()
          .sort((a, b) => (a?.Ordem ?? 9999) - (b?.Ordem ?? 9999))
      : deduped;

    return ordenado.map((item, idx) => ({ ...item, Ordem: idx + 1 }));
  }, []);

  const setTableKeyForcarRemount = useCallback(() => {
    setTableKey(Date.now());
  }, []);

  const aplicarOrdenacaoFromUrl = useCallback(
    listaColunaOrdemParam => {
      if (!listaColunaOrdemParam?.length) return false;

      const normalizada = normalizarListaColunaOrdem(listaColunaOrdemParam);
      setListaColunaOrdem(normalizada);
      ignorarPrimeiroHandleChangeRef.current = true;
      setTableKeyForcarRemount();
      return true;
    },
    [normalizarListaColunaOrdem, setTableKeyForcarRemount]
  );

  const obterOrdenacaoPadrao = useCallback(
    colunaChave => {
      if (!configuracao || !colunaChave) return;
      const ordenacoes = configuracao.VisaoColunas?.map(item => {
        if (item.Ordenacao)
          return {
            ColunaOrdem: item.FonteDados,
            TipoOrdem: item.Ordenacao.TipoOrdenacao,
            Ordem: item.Ordenacao.Ordem,
          };
        return null;
      })?.filter(Boolean);

      if (ordenacoes?.length)
        setListaColunaOrdem(normalizarListaColunaOrdem(ordenacoes));
      else
        setListaColunaOrdem(
          normalizarListaColunaOrdem([
            {
              ColunaOrdem: colunaChave.FonteDados,
              TipoOrdem: 'ascend',
              Ordem: 1,
            },
          ])
        );

      setTableKeyForcarRemount();
    },
    [configuracao, normalizarListaColunaOrdem, setTableKeyForcarRemount]
  );

  const removerColunaOrdenacao = useCallback(
    colunaOrdem => {
      if (!colunaOrdem) return;

      setListaColunaOrdem(prev =>
        normalizarListaColunaOrdem(
          (prev || []).filter(item => item?.ColunaOrdem !== colunaOrdem)
        )
      );
      setTableKeyForcarRemount();
    },
    [normalizarListaColunaOrdem, setTableKeyForcarRemount]
  );

  const handleTableChange = useCallback(
    (pagination, filters, sorter) => {
      const isSorterArray = Array.isArray(sorter);
      const sorterSemOrdem = isSorterArray
        ? sorter.every(s => !s?.order)
        : !sorter?.order;

      if (ignorarPrimeiroHandleChangeRef.current && sorterSemOrdem) {
        ignorarPrimeiroHandleChangeRef.current = false;
        return;
      }

      if (ignorarPrimeiroHandleChangeRef.current && !sorterSemOrdem) {
        ignorarPrimeiroHandleChangeRef.current = false;
      }
      const toColumnKey = s => {
        const raw =
          s?.field ??
          s?.columnKey ??
          s?.column?.key ??
          s?.column?.dataIndex ??
          null;

        if (Array.isArray(raw)) return raw.join('.');
        if (raw === null || raw === undefined) return null;
        return String(raw).trim();
      };

      // Caso 1: Antd envia array quando ha multi-sort (shift+click / multiple habilitado).
      if (isSorterArray) {
        setListaColunaOrdem(prev => {
          const ativos = sorter
            .map(s => ({
              key: toColumnKey(s),
              order: s?.order || null,
              multiple: s?.column?.sorter?.multiple,
            }))
            .filter(s => s.key && s.order);

          // Se o usuario limpou tudo
          if (!ativos.length) {
            return [];
          }

          const podeOrdenarPorMultiple = ativos.every(a =>
            Number.isFinite(a?.multiple)
          );
          const ativosOrdenados = podeOrdenarPorMultiple
            ? ativos.slice().sort((a, b) => a.multiple - b.multiple)
            : ativos;

          const merged = ativosOrdenados.map((item, idx) => ({
            ColunaOrdem: item.key,
            TipoOrdem: item.order,
            Ordem: idx + 1,
          }));

          const normalizada = normalizarListaColunaOrdem(merged);
          return normalizada;
        });
        return;
      }

      const coluna = toColumnKey(sorter);
      const ordem = sorter?.order || null;
      const hasMultiplasAtivas = (listaColunaOrdem || []).length > 1;
      const sorterMultiple = sorter?.column?.sorter?.multiple;
      const isEventoSingleComMultiAtivo =
        hasMultiplasAtivas && Number.isFinite(sorterMultiple);

      if (!coluna) return;

      setListaColunaOrdem(prev => {
        if (isEventoSingleComMultiAtivo) {
          const next = !ordem
            ? []
            : [{ ColunaOrdem: coluna, TipoOrdem: ordem, Ordem: 1 }];
          const normalizada = normalizarListaColunaOrdem(next);
          return normalizada;
        }
        // Remocao da ordenacao dessa coluna
        if (!ordem) {
          const filtered = (prev || [])
            .filter(item => item.ColunaOrdem !== coluna)
            .map((item, idx) => ({ ...item, Ordem: idx + 1 }));
          const normalizada = normalizarListaColunaOrdem(filtered);
          return normalizada;
        }

        const semColuna = (prev || []).filter(
          item => item.ColunaOrdem !== coluna
        );

        // Regra: a coluna clicada vira prioridade 1; as demais mantem a ordem relativa.
        const merged = [
          { ColunaOrdem: coluna, TipoOrdem: ordem },
          ...semColuna.map(i => ({
            ColunaOrdem: i.ColunaOrdem,
            TipoOrdem: i.TipoOrdem,
          })),
        ].map((item, idx) => ({ ...item, Ordem: idx + 1 }));

        const normalizada = normalizarListaColunaOrdem(merged);
        return normalizada;
      });
    },
    [listaColunaOrdem, normalizarListaColunaOrdem]
  );

  return {
    aplicarOrdenacaoFromUrl,
    handleTableChange,
    listaColunaOrdem,
    normalizarListaColunaOrdem,
    obterOrdenacaoPadrao,
    removerColunaOrdenacao,
    setListaColunaOrdem,
    tableKey,
  };
}
